import React from 'react';
import PropTypes from 'prop-types';
import { Box, Skeleton } from '@mui/material';
import Card from '../../atoms/Card/Card';
import {
  operationalComponentSurfaces,
  operationalRadii
} from '../../../design-system/tokens';

/**
 * ArticleCardSkeleton - Componente de carregamento (skeleton loader)
 * para exibir durante o carregamento dos artigos
 *
 * Variantes suportadas: detailed, list, compact
 */
const ArticleCardSkeleton = ({ variant = 'detailed', sx: externalSx }) => {
  
  // Skeleton para variante LIST
  if (variant === 'list') {
    return (
      <Card
        variant="list"
        sx={{
          borderRadius: `${operationalRadii.surface}px`,
          height: '140px !important',
          minHeight: '140px !important',
          maxHeight: '140px !important',
          overflow: 'hidden !important',
          ...(externalSx || {})
        }}
      >
        <Box sx={{ display: 'flex', gap: 0, height: '100%' }}>
          {/* Imagem lateral skeleton */}
          <Box sx={{
            width: 160,
            flexShrink: 0,
            borderRadius: `${operationalRadii.surface}px 0 0 ${operationalRadii.surface}px`,
            overflow: 'hidden'
          }}>
            <Skeleton
              variant="rectangular"
              width={160}
              height="100%"
              animation="wave"
            />
          </Box>

          {/* Separador */}
          <Box sx={{ width: 2, bgcolor: 'divider', flexShrink: 0 }} />

          {/* Conteúdo */}
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, overflow: 'hidden' }}>
            {/* Área superior: Conteúdo + Ações */}
            <Box sx={{ display: 'flex', gap: 1.5, p: 1.5, flex: 1, minHeight: 0 }}>
              <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                {/* Título skeleton - 2 linhas */}
                <Skeleton variant="text" width="85%" height={20} animation="wave" sx={{ fontSize: '0.95rem' }} />
                <Skeleton variant="text" width="70%" height={20} animation="wave" sx={{ fontSize: '0.95rem' }} />

                {/* Metadados skeleton - Favicon + Nome + Data + Tier + Plataforma */}
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                  {/* Favicon + Nome */}
                  <Skeleton variant="rectangular" width={16} height={16} animation="wave" />
                  <Skeleton variant="text" width={80} height={14} animation="wave" />
                  {/* Separador */}
                  <Skeleton variant="text" width={8} height={14} animation="wave" />
                  {/* Data */}
                  <Skeleton variant="text" width={70} height={14} animation="wave" />
                  {/* Separador */}
                  <Skeleton variant="text" width={8} height={14} animation="wave" />
                  {/* Tier */}
                  <Skeleton variant="rounded" width={50} height={18} animation="wave" />
                  {/* Separador */}
                  <Skeleton variant="text" width={8} height={14} animation="wave" />
                  {/* Plataforma */}
                  <Skeleton variant="rounded" width={60} height={18} animation="wave" />
                </Box>
              </Box>

              {/* Botões de ação skeleton */}
              <Box sx={{ flexShrink: 0, alignSelf: 'flex-start' }}>
                <Skeleton variant="circular" width={32} height={32} animation="wave" />
              </Box>
            </Box>

            {/* Footer skeleton com background blur */}
            <Box sx={{
              backgroundColor: operationalComponentSurfaces.stage,
              px: 2,
              py: 1,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 2
            }}>
              {/* Métricas skeleton - 3 a 4 métricas */}
              <Box sx={{ display: 'flex', gap: 3, alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Skeleton variant="rectangular" width={16} height={16} animation="wave" />
                  <Skeleton variant="text" width={60} height={16} animation="wave" />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Skeleton variant="rectangular" width={16} height={16} animation="wave" />
                  <Skeleton variant="text" width={50} height={16} animation="wave" />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Skeleton variant="rectangular" width={16} height={16} animation="wave" />
                  <Skeleton variant="text" width={55} height={16} animation="wave" />
                </Box>
              </Box>
              {/* Tags skeleton */}
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                <Skeleton variant="rounded" width={50} height={20} animation="wave" />
                <Skeleton variant="rounded" width={60} height={20} animation="wave" />
              </Box>
            </Box>
          </Box>
        </Box>
      </Card>
    );
  }
  
  // Skeleton para variante DETAILED (padrão)
  if (variant === 'detailed' || variant === 'compact') {
    return (
      <Card 
        variant={variant} 
        sx={{ 
          borderRadius: `${operationalRadii.surface}px`,
          overflow: 'hidden',
          height: '460px !important',
          minHeight: '460px !important',
          maxHeight: '460px !important',
          display: 'flex',
          flexDirection: 'column',
          ...(externalSx || {})
        }}
      >
        {/* Imagem skeleton */}
        <Box sx={{ flexShrink: 0 }}>
          <Skeleton 
            variant="rectangular" 
            width="100%" 
            height={200} 
            animation="wave"
          />
        </Box>
        
        {/* Conteúdo */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', p: 2, pb: 0, minHeight: 0, overflow: 'hidden' }}>
          {/* Título skeleton */}
          <Skeleton variant="text" width="90%" height={28} animation="wave" />
          <Skeleton variant="text" width="75%" height={28} animation="wave" />
          
          {/* Metadados skeleton */}
          <Box sx={{ display: 'flex', gap: 1, mt: 1.5, alignItems: 'center' }}>
            <Skeleton variant="circular" width={16} height={16} animation="wave" />
            <Skeleton variant="text" width={120} height={20} animation="wave" />
            <Skeleton variant="text" width={80} height={20} animation="wave" />
          </Box>
          
          {/* Tags skeleton */}
          <Box sx={{ display: 'flex', gap: 0.5, mt: 1.5 }}>
            <Skeleton variant="rounded" width={60} height={24} animation="wave" />
            <Skeleton variant="rounded" width={80} height={24} animation="wave" />
            <Skeleton variant="rounded" width={50} height={24} animation="wave" />
          </Box>
        </Box>
        
        {/* Footer skeleton - métricas em linha horizontal */}
        <Box sx={{
          px: 1.5,
          py: 1,
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          gap: 1.5,
          flexWrap: 'wrap',
          flexShrink: 0,
          backgroundColor: operationalComponentSurfaces.stage
        }}>
          {/* Valoração skeleton */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Skeleton variant="rectangular" width={16} height={16} animation="wave" />
            <Skeleton variant="text" width={80} height={16} animation="wave" />
          </Box>

          {/* Views skeleton */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Skeleton variant="rectangular" width={16} height={16} animation="wave" />
            <Skeleton variant="text" width={60} height={16} animation="wave" />
          </Box>

          {/* Inscritos skeleton */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Skeleton variant="rectangular" width={16} height={16} animation="wave" />
            <Skeleton variant="text" width={70} height={16} animation="wave" />
          </Box>
        </Box>
      </Card>
    );
  }

  return null;
};

ArticleCardSkeleton.propTypes = {
  variant: PropTypes.oneOf(['detailed', 'compact', 'list']),
  sx: PropTypes.object
};

export default ArticleCardSkeleton;

